import { create } from 'zustand';

export type SuppliersExhibitionType =
    | 'PRODUCT_SUPPLIERS_CLUSTER'
    | 'PRODUCT_SUPPLIERS_STORE'
    | 'PRODUCT_SUPPLIERS_NETWORK';

export type GroupByType = 'CLUSTER' | 'STORE' | 'NETWORK';

export type ProductType = 'PRODUCT';

type ExhibitionCombinationKey = `${GroupByType}_${ProductType}`;

interface ExhibitionGrouping {
    groupBy: GroupByType;
    productType: ProductType;
}

interface SuppliersExhibitionStore {
    suppliersExhibitionType: SuppliersExhibitionType;
    setSuppliersExhibitionType: (type: SuppliersExhibitionType) => void;
    updateExhibitionType: (groupBy: GroupByType, productType: ProductType) => void;
}

// Este mapa é parcial de propósito: nem toda combinação de agrupamento e tipo de produto que dá
// para escrever tem tradução aqui, e um tipo de produto novo entra sem nenhuma. Declará-lo total
// fazia o verificador de tipos tratar a guarda lá embaixo como código morto, e aí um erro de
// digitação de quem chama virava uma troca de agrupamento que silenciosamente não acontecia.
const EXHIBITION_TYPE_MAP: Partial<Record<ExhibitionCombinationKey, SuppliersExhibitionType>> = {
    'CLUSTER_PRODUCT': 'PRODUCT_SUPPLIERS_CLUSTER',
    'STORE_PRODUCT': 'PRODUCT_SUPPLIERS_STORE',
    'NETWORK_PRODUCT': 'PRODUCT_SUPPLIERS_NETWORK',
};

// O agrupamento em que a tela abre.
const OPENING_EXHIBITION_GROUPING: ExhibitionGrouping = { groupBy: 'NETWORK', productType: 'PRODUCT' };

// O mapa inverso também é parcial na leitura, e por um motivo de tempo de execução: a função que
// grava o tipo de exibição é exportada sem validação nenhuma, então um valor fora da união chega ao
// estado sem nada no meio e cai fora deste mapa. A cláusula de satisfação continua exigindo entrada
// para cada valor da união — parcial para quem lê, completo para quem escreve.
const REVERSE_EXHIBITION_TYPE_MAP: Partial<Record<SuppliersExhibitionType, ExhibitionGrouping>> = {
    'PRODUCT_SUPPLIERS_CLUSTER': { groupBy: 'CLUSTER', productType: 'PRODUCT' },
    'PRODUCT_SUPPLIERS_STORE': { groupBy: 'STORE', productType: 'PRODUCT' },
    'PRODUCT_SUPPLIERS_NETWORK': OPENING_EXHIBITION_GROUPING,
} satisfies Record<SuppliersExhibitionType, ExhibitionGrouping>;

// Recua para o agrupamento de abertura em vez de ler campo de indefinido: o seletor de agrupamento
// é o único caminho para a visão rede, e um estouro aqui não degrada um pedaço da tela, derruba o
// cabeçalho inteiro.
const resolveExhibitionGrouping = (type: SuppliersExhibitionType): ExhibitionGrouping =>
    REVERSE_EXHIBITION_TYPE_MAP[type] ?? OPENING_EXHIBITION_GROUPING;

export const useSuppliersExhibitionStore = create<SuppliersExhibitionStore>((set) => ({
    // A tela abre agrupada por toda a rede, e o agrupamento escolhido continua sem persistência:
    // recarregar a página devolve o comprador a este valor, e não ao último que ele escolheu.
    suppliersExhibitionType: 'PRODUCT_SUPPLIERS_NETWORK',

    setSuppliersExhibitionType: (type) => set({ suppliersExhibitionType: type }),

    updateExhibitionType: (groupBy, productType) => {
        const combinationKey: ExhibitionCombinationKey = `${groupBy}_${productType}`;
        const newType = EXHIBITION_TYPE_MAP[combinationKey];

        if (newType) {
            set({ suppliersExhibitionType: newType });
        }
    },
}));

export const selectorSuppliersExhibitionType = (state: SuppliersExhibitionStore) => state.suppliersExhibitionType;
export const selectorCurrentGroupBy = (state: SuppliersExhibitionStore) =>
    resolveExhibitionGrouping(state.suppliersExhibitionType).groupBy;
export const selectorCurrentProductType = (state: SuppliersExhibitionStore) =>
    resolveExhibitionGrouping(state.suppliersExhibitionType).productType;
